import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { TokenService } from '../services/token.service';

/**
 * Sanctum SPA (cookie) auth wiring for our own API only.
 *
 *  - `withCredentials: true` so the browser sends the httpOnly session cookie +
 *    the XSRF-TOKEN cookie cross-origin (ui host → api host).
 *  - On state-changing requests, echoes the XSRF-TOKEN cookie back as the
 *    `X-XSRF-TOKEN` header (Angular's built-in XSRF support does NOT do this for
 *    cross-origin/absolute URLs, so we do it explicitly).
 *  - A bearer token is still attached if one exists (non-SPA / switch-client
 *    fallback); harmless in pure SPA mode where there is no token.
 *
 * Scoped to `environment.apiUrl` (and the sibling `/sanctum/*` path) so
 * credentials never leak to a third-party origin.
 */
function readCookie(name: string): string | null {
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const apiOrigin = environment.apiUrl.replace(/\/api\/v1\/?$/, '');
  const isOwnApi = req.url.startsWith(environment.apiUrl) || req.url.startsWith(`${apiOrigin}/sanctum/`);

  if (!isOwnApi) {
    return next(req);
  }

  const setHeaders: Record<string, string> = { Accept: 'application/json' };

  const isMutating = !['GET', 'HEAD', 'OPTIONS'].includes(req.method.toUpperCase());
  if (isMutating) {
    const xsrf = readCookie('XSRF-TOKEN');
    if (xsrf) {
      setHeaders['X-XSRF-TOKEN'] = xsrf;
    }
  }

  const token = inject(TokenService).getToken();
  if (token && !req.url.includes('/auth/login')) {
    setHeaders['Authorization'] = `Bearer ${token}`;
  }

  return next(req.clone({ setHeaders, withCredentials: true }));
};
